import React, { useRef, useEffect, useState } from "react";
import clsx from "clsx";

import { ITeachingMaterial } from "../../../../state";
import { TEACHING_MATERIAL_KEYWORDS } from "../../../../constants/teaching_materials";
import { ReactComponent as ArrowLeftIcon } from "../../../../assets/svg/arrow_left_3.svg";
import { ReactComponent as NoProgressIcon } from "../../../../assets/svg/no_progress.svg";
import { ReactComponent as HalfProgressIcon } from "../../../../assets/svg/half_progress.svg";
import { ReactComponent as CompleteIcon } from "../../../../assets/svg/complete.svg";
import { CategoryType } from "../index";
import styles from "../SelectPage.module.scss";

const getProgressIcon = (
  material: ITeachingMaterial,
  targetClassIds: number[],
) => {
  if (material.title.includes(TEACHING_MATERIAL_KEYWORDS.ORIENTATION)) {
    return null;
  }

  const lessons = material.lessons || [];
  const validLessons = lessons.filter(l => l.name && l.name !== " ");

  if (validLessons.length === 0) return <NoProgressIcon />;

  const completedCount = validLessons.filter(lesson =>
    lesson.answer_statuses?.some(
      status =>
        targetClassIds.includes(status.klass_id) && status.has_enough_answers,
    ),
  ).length;

  if (completedCount === 0) {
    return <NoProgressIcon />;
  } else if (completedCount === validLessons.length) {
    return <CompleteIcon />;
  } else {
    return <HalfProgressIcon />;
  }
};

interface ISidebarProps {
  classList: string[];
  currentMaterials: ITeachingMaterial[];
  viewingMaterialId: number | null;
  activeModalId: number | null;
  activeTab: CategoryType;
  targetClassIds: number[];
  showTabs: boolean;
  hasMiddle: boolean;
  hasHigh: boolean;
  hasCore: boolean;
  hasBase: boolean;
  onBackToClass: () => void;
  onTabClick: (tab: CategoryType) => void;
  onItemClick: (
    e: React.MouseEvent<HTMLDivElement>,
    materialId: number,
  ) => void;
}

const Sidebar = ({
  classList,
  currentMaterials,
  viewingMaterialId,
  activeModalId,
  activeTab,
  targetClassIds,
  showTabs,
  hasMiddle,
  hasHigh,
  hasCore,
  hasBase,
  onBackToClass,
  onTabClick,
  onItemClick,
}: ISidebarProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      if (listRef.current) {
        const { scrollHeight, clientHeight } = listRef.current;
        setHasScroll(scrollHeight > clientHeight);
      }
    };
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [currentMaterials]);

  const showVersionHeader =
    new Set(currentMaterials.map(m => m.package_version).filter(Boolean))
      .size >= 2;

  return (
    <aside className={`${styles.sidebar} ${hasScroll ? styles.hasScroll : ""}`}>
      <div className={styles.sidebarHeader}>
        <div className={styles.navigateBack} onClick={onBackToClass}>
          <ArrowLeftIcon />
          <span>クラス選択に戻る</span>
        </div>
        <div className={styles.classInfo}>
          <span className={styles.label}>選択中のクラス</span>
          <div className={styles.className}>{classList.join(",")}</div>
        </div>
        {showTabs && (
          <div className={styles.categoryTabs}>
            {hasMiddle && (
              <div
                className={`${styles.tabItem} ${
                  activeTab === "middle" ? styles.activeTab : ""
                }`}
                onClick={() => onTabClick("middle")}
              >
                <div className={styles.radioCircle}>
                  {activeTab === "middle" && <div className={styles.dot} />}
                </div>
                <span>中学</span>
              </div>
            )}
            {hasHigh && (
              <div
                className={`${styles.tabItem} ${
                  activeTab === "high" ? styles.activeTab : ""
                }`}
                onClick={() => onTabClick("high")}
              >
                <div className={styles.radioCircle}>
                  {activeTab === "high" && <div className={styles.dot} />}
                </div>
                <span>高校</span>
              </div>
            )}
            {hasCore && (
              <div
                className={`${styles.tabItem} ${
                  activeTab === "core" ? styles.activeTab : ""
                }`}
                onClick={() => onTabClick("core")}
              >
                <div className={styles.radioCircle}>
                  {activeTab === "core" && <div className={styles.dot} />}
                </div>
                <span>CORE</span>
              </div>
            )}
            {hasBase && (
              <div
                className={`${styles.tabItem} ${
                  activeTab === "base" ? styles.activeTab : ""
                }`}
                onClick={() => onTabClick("base")}
              >
                <div className={styles.radioCircle}>
                  {activeTab === "base" && <div className={styles.dot} />}
                </div>
                <span>BASE</span>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.materialList} ref={listRef}>
        {currentMaterials.map((material, index) => {
          const isOrientation = material.title.includes(
            TEACHING_MATERIAL_KEYWORDS.ORIENTATION,
          );
          const isViewing = viewingMaterialId === material.id;
          const isModalOpen = activeModalId === material.id;
          const isActive = activeModalId !== null ? isModalOpen : isViewing;
          const prevVersion =
            index > 0 ? currentMaterials[index - 1].package_version : null;
          const showHeader =
            showVersionHeader &&
            material.package_version &&
            material.package_version !== prevVersion;

          return (
            <React.Fragment key={material.id}>
              {showHeader && (
                <div className={styles.versionHeader}>
                  ver {material.package_version}
                </div>
              )}
              <div
                className={clsx(
                  styles.materialItem,
                  isActive && styles.active,
                  isModalOpen && styles.activeModal,
                )}
                style={{
                  borderColor:
                    !isActive && isViewing ? "var(--Gray-4, #CED4DA)" : "",
                }}
                onClick={e => {
                  e.stopPropagation();
                  onItemClick(e, material.id);
                }}
              >
                <div
                  className={styles.materialThumb}
                  style={{
                    backgroundImage: `url(${material.image_url || ""})`,
                  }}
                >
                  <div className={styles.progressIcon}>
                    {getProgressIcon(material, targetClassIds)}
                  </div>
                </div>
                <div className={styles.materialTitle}>
                  {isOrientation ? material.title : material.name}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </aside>
  );
};

export default Sidebar;
